import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { DateRangePicker } from '@/components/DateRangePicker';
import { Stat } from '@/components/Stat';
import { DailyCostChart, GroupCostBarChart, GroupCostColumnChart } from '@/components/charts';
import { RecentRequestsTable } from '@/components/RecentRequestsTable';
import { daysAgo, fmtNumber, fmtTokens, fmtUsd } from '@/lib/format';
import type { UsageGroupRow } from '@/types';

export function AdminUsage() {
  const api = useApi();
  const [range, setRange] = useState({ from: daysAgo(30), to: daysAgo(0) });
  // '' = every caller. The API already accepts callerId on each of these endpoints, so filtering
  // happens in SQL rather than by trimming rows in the browser.
  const [callerId, setCallerId] = useState('');
  const caller = callerId || undefined;

  // Wrapped rather than passed by reference: adminListCallers takes an optional status, and
  // React Query would hand it the query context as that argument.
  const callers = useQuery({ queryKey: ['admin-callers'], queryFn: () => api.adminListCallers() });

  const daily = useQuery({
    queryKey: ['admin-usage-daily', range.from, range.to, callerId],
    queryFn: () => api.adminUsageDaily(range.from, range.to, caller)
  });
  const recent = useQuery({
    queryKey: ['admin-recent-requests', callerId],
    queryFn: () => api.adminRecentRequests(100, caller)
  });
  // Deliberately NOT filtered: this is the breakdown the filter is chosen from, so it stays
  // whole-gateway to keep the comparison meaningful. It is hidden while a filter is active.
  const byCaller = useQuery({
    queryKey: ['admin-usage-by-caller', range.from, range.to],
    queryFn: () => api.adminUsageByCaller(range.from, range.to)
  });
  const byModel = useQuery({
    queryKey: ['admin-usage-by-model', range.from, range.to, callerId],
    queryFn: () => api.adminUsageByModel(range.from, range.to, caller)
  });
  const toolUsage = useQuery({
    queryKey: ['admin-tool-usage', range.from, range.to, callerId],
    queryFn: () => api.adminToolUsage(range.from, range.to, caller)
  });
  const toolInvocations = useQuery({
    queryKey: ['admin-tool-invocations', callerId],
    queryFn: () => api.adminToolInvocations(50, caller)
  });

  const selectedCaller = (callers.data ?? []).find(c => c.id === callerId);

  const dailyRows = daily.data ?? [];
  const callerRows = byCaller.data ?? [];
  const modelRows = byModel.data ?? [];

  const totalCost = dailyRows.reduce((s, d) => s + d.costUsd, 0);
  const totalCalls = dailyRows.reduce((s, d) => s + d.calls, 0);
  const totalTokens = dailyRows.reduce((s, d) => s + d.inputTokens + d.outputTokens, 0);
  const topCaller = callerRows.length === 0
    ? null
    : callerRows.reduce((best, r) => (r.costUsd > best.costUsd ? r : best));

  const error = (daily.error ?? byCaller.error ?? byModel.error) as Error | null;
  const loading = daily.isLoading || byCaller.isLoading || byModel.isLoading;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Usage</h1>
          <div className="sub">
            {selectedCaller
              ? `${selectedCaller.displayName} only, ${range.from} to ${range.to}`
              : `Gateway-wide spend and volume, ${range.from} to ${range.to}`}
          </div>
        </div>
      </div>

      <DateRangePicker from={range.from} to={range.to} onApply={(from, to) => setRange({ from, to })} />

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ marginBottom: 0, minWidth: 260 }}>
            <label htmlFor="usage-caller">Caller</label>
            <select
              id="usage-caller"
              className="select"
              style={{ width: '100%' }}
              value={callerId}
              disabled={callers.isLoading}
              onChange={e => setCallerId(e.target.value)}>
              <option value="">All callers</option>
              {[...(callers.data ?? [])]
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map(c => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}{c.type === 'System' ? ' (system)' : ''}
                  </option>
                ))}
            </select>
          </div>
          {selectedCaller && (
            <button className="btn-ghost btn-sm" onClick={() => setCallerId('')}>Clear filter</button>
          )}
          <div className="hint" style={{ marginLeft: 'auto', marginTop: 0 }}>
            {selectedCaller
              ? 'Every panel below is scoped to this caller.'
              : 'Pick a caller to scope the whole page to them.'}
          </div>
        </div>
      </div>

      {error && <div className="error-banner">Could not load usage: {error.message}</div>}
      {loading && <div className="placeholder">Loading usage...</div>}

      {!loading && !error && (
        <>
          <div className="stat-grid">
            <Stat label="Total spend" value={fmtUsd(totalCost)} />
            <Stat label="Total calls" value={fmtNumber(totalCalls)} />
            <Stat label="Tokens in + out" value={fmtTokens(totalTokens)} />
            {selectedCaller
              ? <Stat label="Caller" value={selectedCaller.displayName} sub={selectedCaller.type === 'System' ? 'System caller' : selectedCaller.email ?? 'User'} />
              : <Stat
                  label="Top caller by spend"
                  value={topCaller ? fmtUsd(topCaller.costUsd) : '—'}
                  sub={topCaller?.group}
                />}
          </div>

          <div className="card chart-block" style={{ marginBottom: 14 }}>
            <div className="chart-title">Daily cost (USD)</div>
            {dailyRows.length === 0
              ? <div className="placeholder">No usage in this period.</div>
              : <DailyCostChart data={dailyRows} />}
          </div>

          <div className={selectedCaller ? undefined : 'grid-2'}>
            <div className="card chart-block">
              <div className="chart-title">Cost by model (USD)</div>
              {modelRows.length === 0
                ? <div className="placeholder">No usage in this period.</div>
                : <GroupCostBarChart data={modelRows} label="Cost" />}
            </div>
            {!selectedCaller && (
              <div className="card chart-block">
                <div className="chart-title">Cost by caller (USD)</div>
                {callerRows.length === 0
                  ? <div className="placeholder">No usage in this period.</div>
                  : <GroupCostColumnChart data={callerRows} label="Cost" />}
              </div>
            )}
          </div>

          <div className={selectedCaller ? undefined : 'grid-2'}>
            {!selectedCaller && <UsageGroupTable title="By caller" rows={callerRows} groupHeader="Caller" />}
            <UsageGroupTable title="By model" rows={modelRows} groupHeader="Model" />
          </div>

          {(toolUsage.data ?? []).length > 0 && (
            <div className="grid-2" style={{ marginTop: 14 }}>
              <div className="card">
                <div className="card-header"><h3>MCP tool usage</h3></div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Server / tool</th>
                      <th className="num">Calls</th>
                      <th className="num">Failures</th>
                      <th className="num">Avg ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(toolUsage.data ?? []).map((r, i) => (
                      <tr key={i}>
                        <td>
                          <div className="row-title">{r.serverName}</div>
                          <div className="row-sub">{r.toolName}</div>
                        </td>
                        <td className="num">{fmtNumber(r.calls)}</td>
                        <td className="num" style={r.failures > 0 ? { color: 'var(--kt-red)' } : undefined}>{fmtNumber(r.failures)}</td>
                        <td className="num">{fmtNumber(r.avgDurationMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card">
                <div className="card-header"><h3>Recent tool invocations</h3></div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Caller</th>
                      <th>Server / tool</th>
                      <th className="num">ms</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(toolInvocations.data ?? []).map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '.8rem' }}>{new Date(r.timestampUtc).toLocaleString()}</td>
                        <td>{r.callerName}</td>
                        <td>
                          <div className="row-title" style={{ fontSize: '.85rem' }}>{r.server}</div>
                          <div className="row-sub">{r.tool}</div>
                        </td>
                        <td className="num">{fmtNumber(r.durationMs)}</td>
                        <td>{r.success ? '✓' : <span style={{ color: 'var(--kt-red)' }}>✗</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <RecentRequestsTable
        rows={recent.data ?? []}
        isLoading={recent.isLoading}
        error={recent.error as Error | null}
        showCaller
      />
    </>
  );
}

function UsageGroupTable({ title, rows, groupHeader }: {
  title: string;
  rows: UsageGroupRow[];
  groupHeader: string;
}) {
  const sorted = [...rows].sort((a, b) => b.costUsd - a.costUsd);
  return (
    <div className="card">
      <div className="card-header"><h3>{title}</h3></div>
      <table className="data-table">
        <thead>
          <tr>
            <th>{groupHeader}</th>
            <th className="num">Calls</th>
            <th className="num">Tokens in</th>
            <th className="num">Tokens out</th>
            <th className="num">Cost</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={5} className="placeholder">No usage in this period.</td></tr>
          ) : sorted.map(r => (
            <tr key={r.group}>
              <td><span className="row-title">{r.group}</span></td>
              <td className="num">{fmtNumber(r.calls)}</td>
              <td className="num">{fmtTokens(r.inputTokens)}</td>
              <td className="num">{fmtTokens(r.outputTokens)}</td>
              <td className="num">{fmtUsd(r.costUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
